import Icon from '@/components/ui/AppIcon';

interface TimelineStep {
  phase: string;
  title: string;
  description: string;
  duration: string;
  icon: string;
  tasks: string[];
}

export default function IntegrationTimeline() {
  const steps: TimelineStep[] = [
    {
      phase: "Phase 1",
      title: "Discovery & Planning",
      description: "Initial consultation and technical requirements gathering",
      duration: "1-2 weeks",
      icon: "LightBulbIcon",
      tasks: [
        "Kickoff meeting with integration team",
        "Technical requirements documentation",
        "API access credentials setup",
        "Development environment configuration"
      ]
    },
    {
      phase: "Phase 2",
      title: "Sandbox Integration",
      description: "Development and testing in our sandbox environment",
      duration: "2-4 weeks",
      icon: "BeakerIcon",
      tasks: [
        "API endpoint integration",
        "PNR creation and management testing",
        "Payment gateway configuration",
        "Error handling implementation"
      ]
    },
    {
      phase: "Phase 3",
      title: "Quality Assurance",
      description: "Comprehensive testing and validation",
      duration: "1-2 weeks",
      icon: "ShieldCheckIcon",
      tasks: [
        "End-to-end booking flow testing",
        "Load and performance testing",
        "Security audit and compliance check",
        "User acceptance testing (UAT)"
      ]
    },
    {
      phase: "Phase 4",
      title: "Production Deployment",
      description: "Go-live preparation and launch",
      duration: "1 week",
      icon: "RocketLaunchIcon",
      tasks: [
        "Production credentials setup",
        "Gradual traffic migration",
        "Real-time monitoring activation",
        "Launch announcement and marketing"
      ]
    },
    {
      phase: "Phase 5",
      title: "Ongoing Support",
      description: "Continuous optimization and support",
      duration: "Ongoing",
      icon: "ChatBubbleLeftRightIcon",
      tasks: [
        "24/7 technical support access",
        "Performance monitoring and optimization",
        "Regular feature updates",
        "Quarterly business reviews"
      ]
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="ClockIcon" size={20} className="text-primary" />
            <span className="text-sm font-medium text-primary">Integration Process</span>
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Your Path to Integration
          </h2>
          <p className="text-lg text-muted-foreground">
            A clear, structured approach to get you from signup to production in 5-9 weeks. Our dedicated team guides you through every step.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line - Desktop */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-border -translate-x-1/2" />

          {/* Steps */}
          <div className="space-y-12 lg:space-y-24">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`relative flex flex-col lg:flex-row items-start lg:items-center gap-8 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Content Card */}
                <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                  <div className="bg-card rounded-xl shadow-brand hover:shadow-brand-lg transition-all duration-300 p-6 lg:p-8">
                    {/* Phase Badge */}
                    <div className={`inline-flex items-center space-x-2 bg-primary/10 px-3 py-1 rounded-full mb-4 ${
                      index % 2 === 0 ? 'lg:float-right lg:ml-4' : 'lg:float-left lg:mr-4'
                    }`}>
                      <span className="text-xs font-heading font-semibold text-primary">
                        {step.phase}
                      </span>
                    </div>

                    <h3 className="font-heading text-xl lg:text-2xl font-bold text-foreground mb-2 clear-both">
                      {step.title}
                    </h3>
                    
                    <p className="text-muted-foreground mb-4">
                      {step.description}
                    </p>

                    <div className="flex items-center space-x-2 text-sm text-primary mb-6">
                      <Icon name="ClockIcon" size={16} />
                      <span className="font-medium">{step.duration}</span>
                    </div>

                    {/* Tasks List */}
                    <div className="space-y-2">
                      {step.tasks.map((task, taskIndex) => (
                        <div
                          key={taskIndex}
                          className={`flex items-start space-x-2 ${
                            index % 2 === 0 ? 'lg:flex-row-reverse lg:space-x-reverse' : ''
                          }`}
                        >
                          <Icon
                            name="CheckCircleIcon"
                            size={18}
                            className="text-success mt-0.5 flex-shrink-0"
                          />
                          <span className="text-sm text-foreground">{task}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Center Icon */}
                <div className="relative flex-shrink-0">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 bg-primary rounded-full flex items-center justify-center shadow-lg relative z-10">
                    <Icon
                      name={step.icon as any}
                      size={32}
                      className="text-white"
                    />
                  </div>
                  
                  {/* Connecting Line - Mobile */}
                  {index < steps.length - 1 && (
                    <div className="lg:hidden absolute left-8 top-16 bottom-0 w-0.5 bg-border -mb-12" />
                  )}
                </div>

                {/* Spacer for alternating layout */}
                <div className="hidden lg:block flex-1" />
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl p-8 lg:p-12">
          <Icon name="CalendarIcon" size={48} className="text-primary mx-auto mb-4" />
          <h3 className="font-heading text-2xl lg:text-3xl font-bold text-foreground mb-4">
            Ready to Start Your Integration Journey?
          </h3>
          <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
            Schedule a technical consultation with our integration team to discuss your specific requirements and timeline.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button className="inline-flex items-center space-x-2 bg-primary hover:bg-secondary text-white px-8 py-4 rounded-lg font-heading font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300">
              <Icon name="CalendarIcon" size={20} />
              <span>Schedule Consultation</span>
            </button>
            <button className="inline-flex items-center space-x-2 bg-card hover:bg-surface text-foreground px-8 py-4 rounded-lg font-heading font-semibold text-lg border border-border transition-all duration-300">
              <Icon name="DocumentTextIcon" size={20} />
              <span>Download Integration Guide</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}